import { useState } from 'react';
import { usePageAudio } from './AudioManager';
import styles from './AudioPlayer.module.css'; // import CSS module

/**
 * AudioPlayer component provides a **user-controlled audio toggle button**.
 * 
 * Features:
 * - Plays or pauses audio using the page-scoped AudioManager context
 * - Displays the appropriate label and icon:
 *   - ▶ Play Music when paused
 *   - ⏸ Pause Music when playing
 * - Positions button at the bottom-right corner by default
 * - Handles autoplay restrictions by requiring user interaction
 * 
 * Usage:
 * ```tsx
 * import { AudioManager } from './AudioManager';
 * import { AudioPlayer } from './AudioPlayer';
 * 
 * const Page: React.FC = () => (
 *   <AudioManager src="/audio/song.mp3">
 *     <AudioPlayer />
 *     <Content />
 *   </AudioManager>
 * );
 * ```
 */
export const AudioPlayer: React.FC = () => {
  /** Access the page-scoped audio API */
  const { play, pause } = usePageAudio();

  /** Track whether audio is currently playing */
  const [isPlaying, setIsPlaying] = useState(false);

  /** Handles button clicks to toggle audio playback */
  const handleClick = async () => {
    try {
      if (isPlaying) {
        await pause();
      } else {
        await play();
      }
      setIsPlaying(!isPlaying);
    } catch (err) {
      console.error("Audio play blocked", err);
    }
  };

  return (
    <button onClick={handleClick} className={styles.audioButton}>
      {isPlaying ? '⏸ Pause Music' : '▶ Play Music'}
    </button>
  );
};
